import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  User,
  Bell,
  Shield,
  Globe,
  CreditCard,
  Package,
  Pencil,
  Save,
  X,
  Check,
  AlertCircle,
  Lock,
  RefreshCw,
  ChevronRight,
  Smartphone,
} from 'lucide-react';
import {
  GET_SETTINGS,
  UPDATE_NOTIFICATION_PREFS,
  UPDATE_PAYMENT_SETTINGS,
  UPDATE_PURCHASE_FLOW,
  UPDATE_STORE_PROFILE,
  CHANGE_STORE_PASSWORD,
} from '../graphql/queries';

type TabId = 'profile' | 'security' | 'notifications' | 'b2b' | 'billing' | 'purchase' | 'payment';

interface NotificationPrefs {
  bookingCreated: boolean;
  bookingApproved: boolean;
  bookingRejected: boolean;
  b2bConfirmed: boolean;
  b2bDispatched: boolean;
  b2bDelivered: boolean;
  visitReminder: boolean;
  noCheckIn: boolean;
  weeklyReport: boolean;
  lowStock: boolean;
}

interface BookingPermissions {
  allowDirectSubmit: boolean;
  requirePhoto: boolean;
  enablePackRatio: boolean;
  moqRequired: boolean;
  showMrp: boolean;
}

interface PurchaseFlow {
  defaultQtyMode: 'productWise' | 'ratioWise' | 'allSame';
  approvalFlow: 'manufacturerApproval' | 'autoApprove';
  bookingPermissions: BookingPermissions;
}

interface PaymentSettings {
  razorpay: {
    enabled: boolean;
    keyId: string | null;
    hasSecret: boolean;
    hasWebhookSecret: boolean;
  };
}

interface SettingsData {
  storeId: string;
  storeName: string;
  contactPerson: string | null;
  emailId: string | null;
  mobileNumber: string | null;
  gstin: string | null;
  city: string | null;
  address: string | null;
  portal: string | null;
  prefix: string | null;
  notificationPrefs: NotificationPrefs;
  purchaseFlow: PurchaseFlow;
  b2bConnection: {
    connected: boolean;
    connectorUrl: string | null;
    storeType: string | null;
    installedAt: string | null;
  };
  paymentSettings: PaymentSettings;
}

const TABS_BASE: { id: TabId; label: string; icon: any }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'purchase', label: 'Purchase Flow', icon: Package },
];
const PAYMENT_TAB: { id: TabId; label: string; icon: any } = {
  id: 'payment',
  label: 'Payment',
  icon: CreditCard,
};

interface SettingsProps {
  onNavigate?: (page: string) => void;
}

export const Settings: React.FC<SettingsProps> = ({ onNavigate }) => {
  const [tab, setTab] = useState<TabId>('profile');
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const { data, loading, error, refetch } = useQuery(GET_SETTINGS, { fetchPolicy: 'cache-and-network' });
  const settings: SettingsData | null = data?.getSettings?.settings ?? null;
  const isCustom = settings?.b2bConnection?.storeType === 'custom';
  // Purchase Flow only makes sense for RiEtail-installed stores; custom
  // stores manage everything in-house and get the Payment tab instead.
  const HIDDEN_FOR_CUSTOM: TabId[] = ['purchase'];
  const TABS = isCustom
    ? [...TABS_BASE.filter((t) => !HIDDEN_FOR_CUSTOM.includes(t.id)), PAYMENT_TAB]
    : TABS_BASE;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-black text-text">Settings</h1>
        <p className="text-text2">Manage your store, integrations, and field flow preferences.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Couldn't load settings: {error.message}
        </div>
      )}

      {toast && (
        <div className="fixed top-6 right-6 bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-lg z-50 flex items-center gap-2 text-sm font-semibold">
          <Check size={16} /> {toast}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Tab rail */}
        <aside className="col-span-3">
          <div className="card p-2 space-y-1 sticky top-6">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors text-left
                    ${active ? 'bg-primary text-white shadow-sm' : 'text-text2 hover:bg-gray-50'}`}
                >
                  <t.icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Tab body */}
        <main className="col-span-9 space-y-4">
          {loading && !settings ? (
            <div className="card p-8 text-center text-text3">Loading settings…</div>
          ) : !settings ? (
            <div className="card p-8 text-center text-text3">No store data yet. Re-install the connector first.</div>
          ) : (
            <>
              {tab === 'profile' && <ProfileTab settings={settings} onSaved={(m) => { showToast(m); refetch(); }} />}
              {tab === 'security' && (
                <SecurityTab
                  storeType={settings.b2bConnection.storeType}
                  onSaved={(m) => showToast(m)}
                />
              )}
              {tab === 'notifications' && (
                <NotificationsTab settings={settings} onSaved={(m) => { showToast(m); refetch(); }} />
              )}
              {tab === 'b2b' && <B2BTab settings={settings} onSync={() => { showToast('Refreshed'); refetch(); }} />}
              {tab === 'billing' && <BillingTab onNavigate={onNavigate} />}
              {tab === 'purchase' && (
                <PurchaseFlowTab settings={settings} onSaved={(m) => { showToast(m); refetch(); }} />
              )}
              {tab === 'payment' && isCustom && (
                <PaymentTab settings={settings} onSaved={(m) => { showToast(m); refetch(); }} />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// ── Profile Tab ──────────────────────────────────────────────────
const ProfileTab: React.FC<{ settings: SettingsData; onSaved: (m: string) => void }> = ({ settings, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    storeName: settings.storeName,
    contactPerson: settings.contactPerson || '',
    emailId: settings.emailId || '',
    mobileNumber: settings.mobileNumber || '',
    gstin: settings.gstin || '',
    city: settings.city || '',
    address: settings.address || '',
    prefix: settings.prefix || '',
  });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) {
      setForm({
        storeName: settings.storeName,
        contactPerson: settings.contactPerson || '',
        emailId: settings.emailId || '',
        mobileNumber: settings.mobileNumber || '',
        gstin: settings.gstin || '',
        city: settings.city || '',
        address: settings.address || '',
        prefix: settings.prefix || '',
      });
    }
  }, [settings, editing]);

  const [updateProfile, { loading: saving }] = useMutation(UPDATE_STORE_PROFILE);

  const submit = async () => {
    setErr(null);
    if (!form.storeName.trim()) {
      setErr('Company name is required');
      return;
    }
    const nextPrefix = form.prefix.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]{1,7}$/.test(nextPrefix)) {
      setErr('Employee-ID prefix must be 2-8 characters, start with a letter, use only A-Z and 0-9.');
      return;
    }
    const { data } = await updateProfile({
      variables: { input: { ...form, prefix: nextPrefix } },
    });
    if (data?.updateStoreProfile?.error) {
      setErr(data.updateStoreProfile.message || 'Failed to save');
      return;
    }
    setEditing(false);
    onSaved('Profile updated');
  };

  return (
    <section className="card p-6">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-text">Company Profile</h2>
          <p className="text-xs text-text3 mt-0.5">Information visible to FSRs and on the dashboard.</p>
        </div>
        {editing ? (
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              className="h-9 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2 transition"
            >
              <X size={14} /> Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="h-9 px-4 bg-primary text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow disabled:opacity-60"
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="h-9 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2 transition"
          >
            <Pencil size={14} /> Edit
          </button>
        )}
      </header>

      {err && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{err}</div>}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Company Name" value={form.storeName} editing={editing} onChange={(v) => setForm({ ...form, storeName: v })} />
        <Field label="Contact Person" value={form.contactPerson} editing={editing} onChange={(v) => setForm({ ...form, contactPerson: v })} />
        <Field label="Email" type="email" value={form.emailId} editing={editing} onChange={(v) => setForm({ ...form, emailId: v })} />
        <Field label="Mobile" type="tel" value={form.mobileNumber} editing={editing} onChange={(v) => setForm({ ...form, mobileNumber: v })} />
        <Field label="GSTIN" value={form.gstin} editing={editing} onChange={(v) => setForm({ ...form, gstin: v })} />
        <Field label="City" value={form.city} editing={editing} onChange={(v) => setForm({ ...form, city: v })} />
        <div className="col-span-2">
          <Field label="Address" value={form.address} editing={editing} onChange={(v) => setForm({ ...form, address: v })} multiline />
        </div>

        <div className="col-span-2 mt-2 p-4 rounded-xl border border-border bg-gray-50/60">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[10px] font-black text-text3 uppercase tracking-widest">
                Employee-ID Prefix
              </div>
              <div className="text-[11px] text-text3 mt-0.5">
                Leading segment of every Field User's ID (e.g.{' '}
                <span className="font-mono text-text2">
                  {(form.prefix.trim().toUpperCase() || 'ACME')}-001
                </span>
                ). Must be unique across all stores.
              </div>
            </div>
            <span className="font-mono text-sm font-bold text-text px-3 py-1 bg-white rounded-lg border border-border">
              {form.prefix.trim().toUpperCase() || '—'}
            </span>
          </div>
          {editing ? (
            <input
              className="input-field font-mono text-sm uppercase mt-1"
              value={form.prefix}
              onChange={(e) =>
                setForm({
                  ...form,
                  prefix: e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8),
                })
              }
              placeholder="e.g. ACME"
              maxLength={8}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  editing: boolean;
  onChange: (v: string) => void;
  type?: string;
  multiline?: boolean;
}> = ({ label, value, editing, onChange, type = 'text', multiline }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-widest text-text3">{label}</label>
    {editing ? (
      multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mt-1 w-full bg-[#F8FAFC] border-[1.5px] border-border rounded-lg px-3 py-2 text-sm focus:border-primary focus:bg-white outline-none transition resize-none"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-field mt-1 w-full"
        />
      )
    ) : (
      <div className="mt-1 text-sm text-text font-semibold min-h-[20px]">
        {value || <span className="text-text3 italic font-normal">—</span>}
      </div>
    )}
  </div>
);

// ── Security Tab ─────────────────────────────────────────────────
const SecurityTab: React.FC<{ storeType: string | null; onSaved: (m: string) => void }> = ({
  storeType,
  onSaved,
}) => {
  const isCustom = storeType === 'custom';

  return (
    <section className="space-y-4">
      {isCustom ? (
        <ChangePasswordCard onSaved={onSaved} />
      ) : (
        <div className="card p-6">
          <header className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
              <Lock size={18} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text">Authentication</h2>
              <p className="text-xs text-text3">Admin login is handled by RiEtail B2B (OAuth).</p>
            </div>
          </header>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800 mt-4">
            Field Assist admins authenticate through your RiEtail B2B account. Password rotation and 2FA are managed there.
            Open RiEtail B2B → Settings → Security to manage your sign-in.
          </div>
        </div>
      )}
    </section>
  );
};

const ChangePasswordCard: React.FC<{ onSaved: (m: string) => void }> = ({ onSaved }) => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);
  const [changePassword, { loading }] = useMutation(CHANGE_STORE_PASSWORD);

  const reset = () => setForm({ current: '', next: '', confirm: '' });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.current || !form.next || !form.confirm) {
      setError('All fields are required');
      return;
    }
    if (form.next !== form.confirm) {
      setError('New password and confirmation do not match');
      return;
    }
    if (form.current === form.next) {
      setError('New password must be different from current');
      return;
    }
    try {
      const { data } = await changePassword({
        variables: { currentPassword: form.current, newPassword: form.next },
      });
      const r = data?.changeStorePassword;
      if (r?.error) {
        setError(r.message || 'Failed to change password');
        return;
      }
      onSaved(r?.message || 'Password changed');
      reset();
    } catch (err: any) {
      setError(err?.message || 'Failed to change password');
    }
  };

  return (
    <div className="card p-6">
      <header className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
          <Lock size={18} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-text">Change password</h2>
          <p className="text-xs text-text3">Enter your current password and a new one.</p>
        </div>
      </header>

      <form onSubmit={submit} className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Current password</label>
          <input
            type="password"
            className="input-field mt-1 w-full"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            autoComplete="current-password"
            required
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-text3">New password</label>
          <input
            type="password"
            className="input-field mt-1 w-full"
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            autoComplete="new-password"
            required
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Confirm new password</label>
          <input
            type="password"
            className="input-field mt-1 w-full"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            autoComplete="new-password"
            required
          />
        </div>

        {error && (
          <div className="col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={reset}
            disabled={loading}
            className="h-9 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2 transition"
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={loading}
            className="h-9 px-4 bg-primary text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow disabled:opacity-60"
          >
            <Lock size={14} />
            {loading ? 'Updating…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Notifications Tab ─────────────────────────────────────────────
const NOTIFICATION_LABELS: Record<keyof NotificationPrefs, { label: string; desc: string }> = {
  bookingCreated: { label: 'Booking Created', desc: 'Triggered when a FSR submits a new booking.' },
  bookingApproved: { label: 'Booking Approved', desc: 'When you approve a pending booking.' },
  bookingRejected: { label: 'Booking Rejected', desc: 'When a booking is rejected with a reason.' },
  b2bConfirmed: { label: 'B2B Order Confirmed', desc: 'When the order is accepted by RiEtail B2B.' },
  b2bDispatched: { label: 'B2B Dispatched', desc: 'When the order ships from B2B.' },
  b2bDelivered: { label: 'B2B Delivered', desc: 'When the order is marked delivered in B2B.' },
  visitReminder: { label: 'Visit Reminder', desc: 'Daily reminder to FSRs about today\'s visits.' },
  noCheckIn: { label: 'No Check-in Alert', desc: 'When a FSR hasn\'t checked in by 10 AM.' },
  weeklyReport: { label: 'Weekly Performance Report', desc: 'Sunday summary of FSR performance.' },
  lowStock: { label: 'Low Stock Alert', desc: 'When a popular SKU drops below reorder threshold.' },
};

const NotificationsTab: React.FC<{ settings: SettingsData; onSaved: (m: string) => void }> = ({
  settings,
  onSaved,
}) => {
  const [prefs, setPrefs] = useState<NotificationPrefs>(settings.notificationPrefs);
  const [updateNotif, { loading: saving }] = useMutation(UPDATE_NOTIFICATION_PREFS);

  useEffect(() => {
    setPrefs(settings.notificationPrefs);
  }, [settings.notificationPrefs]);

  const toggle = async (key: keyof NotificationPrefs) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    const { data } = await updateNotif({ variables: { input: { [key]: next[key] } } });
    if (data?.updateNotificationPrefs?.error) {
      // revert
      setPrefs(prefs);
      onSaved('Failed to save');
      return;
    }
    onSaved('Notification updated');
  };

  return (
    <section className="card overflow-hidden">
      <header className="px-6 pt-6 pb-2">
        <h2 className="text-lg font-bold text-text">Notification Preferences</h2>
        <p className="text-xs text-text3 mt-0.5">Channel-agnostic — applies to email + WhatsApp + in-app.</p>
      </header>

      <div className="divide-y divide-border">
        {(Object.keys(NOTIFICATION_LABELS) as (keyof NotificationPrefs)[]).map((key) => {
          const meta = NOTIFICATION_LABELS[key];
          const on = prefs[key];
          return (
            <div key={key} className="p-4 px-6 flex items-center justify-between hover:bg-gray-50/50">
              <div>
                <h3 className="text-sm font-bold text-text">{meta.label}</h3>
                <p className="text-xs text-text3 mt-0.5">{meta.desc}</p>
              </div>
              <button
                disabled={saving}
                onClick={() => toggle(key)}
                className={`relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-primary' : 'bg-gray-300'} disabled:opacity-60`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ── B2B Integration Tab ───────────────────────────────────────────
const B2BTab: React.FC<{ settings: SettingsData; onSync: () => void }> = ({ settings, onSync }) => {
  const conn = settings.b2bConnection;
  return (
    <section className="space-y-4">
      <div className="card p-6">
        <header className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-text">RiEtail B2B Connection</h2>
            <p className="text-xs text-text3 mt-0.5">Status of the connector that syncs distributors, retailers and catalog.</p>
          </div>
          <button
            onClick={onSync}
            className="h-9 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2 transition"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </header>

        <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-gray-50 mb-4">
          <div className={`w-3 h-3 rounded-full ${conn.connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
          <div className="flex-1">
            <div className={`text-sm font-bold ${conn.connected ? 'text-emerald-700' : 'text-red-700'}`}>
              {conn.connected ? 'Connected' : 'Disconnected'}
            </div>
            <div className="text-[11px] text-text3">
              {conn.connected
                ? 'Connector is reachable. Distributors and retailers sync on demand.'
                : 'No connector configured. Re-install via /retailShopInstall to connect.'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ReadonlyField label="Store Type" value={conn.storeType || '—'} />
          <ReadonlyField label="Connector URL" value={conn.connectorUrl || '—'} mono />
          <ReadonlyField
            label="Installed At"
            value={conn.installedAt ? new Date(conn.installedAt).toLocaleString() : '—'}
          />
          <ReadonlyField label="Portal" value={settings.portal || '—'} />
        </div>
      </div>

      <div className="card p-6 opacity-60">
        <h3 className="text-sm font-bold text-text mb-1">Webhook URL & API Key</h3>
        <p className="text-xs text-text3">
          Coming soon — outbound webhooks for booking events and a regenerable API key for headless integrations.
        </p>
      </div>
    </section>
  );
};

const ReadonlyField: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-widest text-text3">{label}</label>
    <div className={`mt-1 text-sm text-text font-semibold break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
  </div>
);

// ── Billing Tab ───────────────────────────────────────────────────
const BillingTab: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => (
  <section className="card p-6">
    <header className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl bg-indigo-100 text-primary flex items-center justify-center">
        <CreditCard size={18} />
      </div>
      <div>
        <h2 className="text-lg font-bold text-text">Billing & Subscription</h2>
        <p className="text-xs text-text3">Plan management lives in the Subscription page.</p>
      </div>
    </header>

    <button
      onClick={() => onNavigate?.('setup-manage')}
      className="mt-4 w-full p-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl flex items-center justify-between hover:opacity-90 active:scale-[0.99] transition"
    >
      <div className="text-left">
        <div className="text-sm font-bold">Go to Subscription</div>
        <div className="text-xs text-white/80 mt-0.5">View plan, FSR seats, and invoices</div>
      </div>
      <ChevronRight size={20} />
    </button>
  </section>
);

// ── Purchase Flow Tab ─────────────────────────────────────────────
const QTY_MODES: { id: PurchaseFlow['defaultQtyMode']; label: string; desc: string }[] = [
  { id: 'productWise', label: 'Product Wise', desc: 'Per-size qty per product separately.' },
  { id: 'ratioWise', label: 'Ratio Wise', desc: 'Enter size ratio once, applies to all selected products.' },
  { id: 'allSame', label: 'All Same', desc: 'One qty applied to every selected product.' },
];

const APPROVAL_FLOWS: { id: PurchaseFlow['approvalFlow']; label: string; desc: string }[] = [
  { id: 'manufacturerApproval', label: 'Manufacturer Approval Required', desc: 'Bookings need approval before pushing to B2B.' },
  { id: 'autoApprove', label: 'Auto-Approve', desc: 'Bookings flow straight to B2B as orders.' },
];

const PERMISSION_LABELS: Record<keyof BookingPermissions, { label: string; desc: string }> = {
  allowDirectSubmit: { label: 'Allow Direct Submit', desc: 'FSRs can submit bookings without admin pre-review.' },
  requirePhoto: { label: 'Require Photo Proof', desc: 'Visit photo required before booking submission.' },
  enablePackRatio: { label: 'Enable Pack/Ratio Ordering', desc: 'Allow Ratio-Wise quantity mode in mobile app.' },
  moqRequired: { label: 'Enforce MOQ', desc: 'Block bookings below the minimum order quantity per SKU.' },
  showMrp: { label: 'Show MRP', desc: 'Display MRP alongside trade price in catalog.' },
};

const PurchaseFlowTab: React.FC<{ settings: SettingsData; onSaved: (m: string) => void }> = ({
  settings,
  onSaved,
}) => {
  const [pf, setPf] = useState<PurchaseFlow>(settings.purchaseFlow);
  const [updatePf, { loading: saving }] = useMutation(UPDATE_PURCHASE_FLOW);

  useEffect(() => {
    setPf(settings.purchaseFlow);
  }, [settings.purchaseFlow]);

  const save = async (input: any, msg: string) => {
    const { data } = await updatePf({ variables: { input } });
    if (data?.updatePurchaseFlow?.error) {
      onSaved('Failed to save');
      return;
    }
    onSaved(msg);
  };

  const setQtyMode = (id: PurchaseFlow['defaultQtyMode']) => {
    setPf({ ...pf, defaultQtyMode: id });
    save({ defaultQtyMode: id }, 'Default qty mode updated');
  };

  const setApprovalFlow = (id: PurchaseFlow['approvalFlow']) => {
    setPf({ ...pf, approvalFlow: id });
    save({ approvalFlow: id }, 'Approval flow updated');
  };

  const togglePermission = (key: keyof BookingPermissions) => {
    const next = { ...pf.bookingPermissions, [key]: !pf.bookingPermissions[key] };
    setPf({ ...pf, bookingPermissions: next });
    save({ bookingPermissions: { [key]: next[key] } }, 'Permission updated');
  };

  return (
    <section className="space-y-4">
      <div className="card p-6">
        <h2 className="text-lg font-bold text-text">Default Quantity Mode</h2>
        <p className="text-xs text-text3 mt-0.5 mb-4">
          The quantity mode FSRs land on first when creating a booking.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {QTY_MODES.map((m) => {
            const on = pf.defaultQtyMode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setQtyMode(m.id)}
                disabled={saving}
                className={`p-4 rounded-xl border-2 text-left transition disabled:opacity-60
                  ${on ? 'border-primary bg-indigo-50' : 'border-border hover:border-primary/40'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Smartphone size={14} className={on ? 'text-primary' : 'text-text3'} />
                  <span className="text-sm font-bold text-text">{m.label}</span>
                </div>
                <p className="text-[11px] text-text3 leading-relaxed">{m.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-bold text-text">Approval Flow</h2>
        <p className="text-xs text-text3 mt-0.5 mb-4">Whether FSR bookings need admin approval.</p>
        <div className="space-y-2">
          {APPROVAL_FLOWS.map((a) => {
            const on = pf.approvalFlow === a.id;
            return (
              <button
                key={a.id}
                onClick={() => setApprovalFlow(a.id)}
                disabled={saving}
                className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 transition disabled:opacity-60
                  ${on ? 'border-primary bg-indigo-50' : 'border-border hover:border-primary/40'}`}
              >
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${on ? 'border-primary bg-primary' : 'border-text3'}`}
                >
                  {on && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-text">{a.label}</div>
                  <div className="text-[11px] text-text3 mt-0.5">{a.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card overflow-hidden">
        <header className="px-6 pt-6 pb-2">
          <h2 className="text-lg font-bold text-text">Booking Permissions</h2>
          <p className="text-xs text-text3 mt-0.5">Fine-grained controls applied to the FSR mobile booking flow.</p>
        </header>
        <div className="divide-y divide-border">
          {(Object.keys(PERMISSION_LABELS) as (keyof BookingPermissions)[]).map((key) => {
            const meta = PERMISSION_LABELS[key];
            const on = pf.bookingPermissions[key];
            return (
              <div key={key} className="p-4 px-6 flex items-center justify-between hover:bg-gray-50/50">
                <div>
                  <h3 className="text-sm font-bold text-text">{meta.label}</h3>
                  <p className="text-xs text-text3 mt-0.5">{meta.desc}</p>
                </div>
                <button
                  disabled={saving}
                  onClick={() => togglePermission(key)}
                  className={`relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-primary' : 'bg-gray-300'} disabled:opacity-60`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── Payment Tab (custom stores only) ─────────────────────────────
const PaymentTab: React.FC<{ settings: SettingsData; onSaved: (m: string) => void }> = ({
  settings,
  onSaved,
}) => {
  const rp = settings.paymentSettings?.razorpay;
  const [form, setForm] = useState({
    enabled: !!rp?.enabled,
    keyId: rp?.keyId || '',
    keySecret: '',
    webhookSecret: '',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatePayment, { loading }] = useMutation(UPDATE_PAYMENT_SETTINGS);

  useEffect(() => {
    setForm({
      enabled: !!rp?.enabled,
      keyId: rp?.keyId || '',
      keySecret: '',
      webhookSecret: '',
    });
  }, [rp?.enabled, rp?.keyId, rp?.hasSecret, rp?.hasWebhookSecret]);

  const submit = async () => {
    setError(null);
    if (form.enabled && !form.keyId.trim()) {
      setError('Razorpay Key ID is required when enabled');
      return;
    }
    if (form.enabled && !rp?.hasSecret && !form.keySecret.trim()) {
      setError('Razorpay Key Secret is required when enabling');
      return;
    }
    const input: any = {
      razorpay: {
        enabled: form.enabled,
        keyId: form.keyId.trim(),
        // Empty string is server-side treated as "keep current", non-empty replaces.
        keySecret: form.keySecret,
        webhookSecret: form.webhookSecret,
      },
    };
    const { data } = await updatePayment({ variables: { input } });
    const r = data?.updatePaymentSettings;
    if (r?.error) {
      setError(r.message || 'Failed to save');
      return;
    }
    onSaved(r?.message || 'Payment settings saved');
    setForm((f) => ({ ...f, keySecret: '', webhookSecret: '' }));
  };

  return (
    <section className="space-y-4">
      <div className="card p-6">
        <header className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 text-primary flex items-center justify-center">
            <CreditCard size={18} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-text">Razorpay</h2>
            <p className="text-xs text-text3 mt-0.5">
              Accept payments via Razorpay for orders placed from the FSR mobile app.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-text2">
            <span>{form.enabled ? 'Enabled' : 'Disabled'}</span>
            <button
              type="button"
              onClick={() => setForm({ ...form, enabled: !form.enabled })}
              className={`relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors ${
                form.enabled ? 'bg-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
                  form.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </header>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">
              Key ID
            </label>
            <input
              className="input-field mt-1 w-full"
              value={form.keyId}
              onChange={(e) => setForm({ ...form, keyId: e.target.value })}
              placeholder="rzp_live_xxxxxxxxxxxx or rzp_test_xxxxxxxxxxxx"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3 flex items-center gap-2">
              Key Secret
              {rp?.hasSecret && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">
                  SAVED
                </span>
              )}
            </label>
            <div className="relative mt-1">
              <input
                type={showSecret ? 'text' : 'password'}
                className="input-field w-full pr-20"
                value={form.keySecret}
                onChange={(e) => setForm({ ...form, keySecret: e.target.value })}
                placeholder={rp?.hasSecret ? 'Leave blank to keep current' : 'Paste secret here'}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2 rounded-md text-[11px] font-semibold text-text2 hover:bg-gray-100"
              >
                {showSecret ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3 flex items-center gap-2">
              Webhook Secret (optional)
              {rp?.hasWebhookSecret && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">
                  SAVED
                </span>
              )}
            </label>
            <input
              type="password"
              className="input-field mt-1 w-full"
              value={form.webhookSecret}
              onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
              placeholder={
                rp?.hasWebhookSecret ? 'Leave blank to keep current' : 'Used to verify Razorpay webhook signatures'
              }
              autoComplete="new-password"
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button onClick={submit} disabled={loading} className="btn-primary disabled:opacity-60">
            <Save size={14} />
            {loading ? 'Saving…' : 'Save payment settings'}
          </button>
        </div>
      </div>

      <div className="card p-4 text-xs text-text2 bg-blue-50 border-blue-100">
        <p className="font-bold text-blue-800 mb-1">How this works</p>
        <p className="leading-relaxed">
          When an FSR submits a booking from the mobile app, your store's Razorpay credentials are used to
          create a hosted Razorpay payment link. The FSR completes payment and the booking is created here
          — no upstream B2B order is pushed. Manage the booking lifecycle (dispatched, delivered) from the
          Bookings page.
        </p>
      </div>
    </section>
  );
};
